import {
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import cx from 'clsx';
import { MenuTrigger, Popover } from 'react-aria-components';
import { useSegment } from '@repo/poly-hooks/segment';
import { FilterButton } from '@repo/design-system-kit';
import type { OperatorKey, PropertyOption } from '../../../types';
import { SELECT_FILTER_TRACKING_EVENT } from '../add-filter-button/add-filter-button';
import styles from './filters-list-item.module.css';

const DELETE_FILTER_TRACKING_EVENT = 'delete_filter';

export interface FiltersListItemProps {
  autoOpen?: boolean;
  values?: string[];
  isHidden?: boolean;
  onOpen?: () => void;
  onClose?: (filter?: { operator: OperatorKey; values: string[] }) => void;
}

export interface FilterListItemComponentProps {
  filter: PropertyOption;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  formattedLabel?: string;
  advancedFilterCount?: number;
  isAdvancedFilter?: boolean;
  isActive?: boolean;

  onOpen?: () => void;
  onClose?: () => void;
  onClear?: () => void;
}

export function FiltersListItem({
  filter,
  open,
  setOpen,
  formattedLabel,
  isActive = false,
  advancedFilterCount = 0,
  isAdvancedFilter = false,
  onClose,
  onOpen,
  children,
  onClear,
}: PropsWithChildren<FilterListItemComponentProps>) {
  // NOTE -- this is needed until we have a better way to handle the portal container in qunit tests
  // https://www.notion.so/qonto/React-popover-library-used-within-react-aria-components-does-mount-popovers-modals-outside-of-qunit--12631ee4c696806e87dbef81e9a27922
  const TEST_PORTAL_CONTAINER = document.getElementById('ember-testing') ?? undefined;

  const segment = useSegment();
  const { formatMessage } = useIntl();

  // Allow position to compute on first render, then lock it so it doesn't jump when the trigger button hides.
  const [shouldUpdate, setShouldUpdate] = useState(true);
  const popoverRef = useRef<HTMLElement>(null);
  const lockedWindowWidthRef = useRef(window.innerWidth);

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      setShouldUpdate(true);
      onClose?.();
      return;
    }

    if (!isActive) {
      segment.track(SELECT_FILTER_TRACKING_EVENT, {
        filter_name: filter.key,
      });
    }

    onOpen?.();
  }

  useEffect(() => {
    if (open && shouldUpdate) {
      const id = requestAnimationFrame(() => {
        setShouldUpdate(false);
        lockedWindowWidthRef.current = window.innerWidth;
      });
      return () => {
        cancelAnimationFrame(id);
      };
    }

    return () => {
      setShouldUpdate(true);
    };
  }, [open, shouldUpdate]);

  useEffect(() => {
    if (!open) return;

    function handleResize() {
      const el = popoverRef.current;
      if (!el) return;

      const rect = el.getBoundingClientRect();
      const isOverflowing =
        rect.right > window.innerWidth ||
        rect.bottom > window.innerHeight ||
        rect.left < 0 ||
        rect.top < 0;

      const hasGrown = window.innerWidth > lockedWindowWidthRef.current;

      if (isOverflowing || hasGrown) {
        setShouldUpdate(true);
      }
    }

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [open]);

  return (
    <MenuTrigger isOpen={open} onOpenChange={handleOpenChange}>
      <FilterButton
        className={styles.filterButton}
        data-filter-key={filter.key}
        data-testid="filter-button"
        hasMenu
        isActive={isActive}
        isOpen={open}
        key={filter.key}
        onClear={() => {
          onClear?.();
          segment.track(DELETE_FILTER_TRACKING_EVENT, {
            filter_name: filter.key,
          });
        }}
      >
        {isAdvancedFilter ? (
          <span className={styles.filterButtonLabel}>
            {/* oxlint-disable-next-line formatjs/no-literal-string-in-jsx --- space after message*/}
            {`${advancedFilterCount} ${formatMessage(
              { id: 'shared-kit.filters.advanced-filter.label' },
              { count: advancedFilterCount }
            )}`}
          </span>
        ) : (
          <span className={styles.filterButtonLabel}>{formattedLabel ?? filter.label}</span>
        )}
      </FilterButton>
      <Popover
        UNSTABLE_portalContainer={TEST_PORTAL_CONTAINER}
        className={cx(styles.dropdown, isAdvancedFilter && styles.advancedFilterDropdown)}
        ref={popoverRef}
        shouldUpdatePosition={shouldUpdate}
      >
        {children}
      </Popover>
    </MenuTrigger>
  );
}
